import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  supportsList: [],
  supportDetail: null,
  isLoading: false,
};

// Create Support
export const createSupport = createAsyncThunk(
  "support/createSupport",
  async ({ name, email, message, phone_number, channel_type }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/supports`;
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      if (bearerToken) {
        config.headers.Authorization = `Bearer ${bearerToken}`;
      }

      const response = await axios.post(
        url,
        { support: { name, email, message, phone_number, channel_type } },
        config
      );
      toast.success("Your support request has been submitted successfully. Our team will get back to you soon!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

// Fetch All Supports
export const getAllSupports = createAsyncThunk(
  "support/getAllSupports",
  async ({ searchValue, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    let url = `${apiBaseUrl}/supports?account_id=${selectedAccount}`;
    if (searchValue) {
      url += `&search=${encodeURIComponent(searchValue)}`;
    }

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

// Get Single Support
export const getSupport = createAsyncThunk(
  "support/getSupport",
  async ({ supportId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/supports/${supportId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

// Update Support
export const updateSupport = createAsyncThunk(
  "support/updateSupport",
  async ({ supportId, support }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/supports/${supportId}`;
    try {
      const response = await axios.patch(
        url,
        { support }, // Nest parameters under 'support' key
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

// Delete Support
export const deleteSupport = createAsyncThunk(
  "support/deleteSupport",
  async ({ supportId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/supports/${supportId}?account_id=${selectedAccount}`;
    try {
      await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Support request deleted successfully!");
      return supportId;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createSupport.pending, (state) => { state.isLoading = true; })
      .addCase(createSupport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.supportsList.push(action.payload);
      })
      .addCase(createSupport.rejected, (state) => { state.isLoading = false; })

      .addCase(getAllSupports.pending, (state) => { state.isLoading = true; })
      .addCase(getAllSupports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.supportsList = action.payload;
      })
      .addCase(getAllSupports.rejected, (state) => { state.isLoading = false; })

      .addCase(getSupport.pending, (state) => { state.isLoading = true; })
      .addCase(getSupport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.supportDetail = action.payload;
      })
      .addCase(getSupport.rejected, (state) => { state.isLoading = false; })

      .addCase(updateSupport.pending, (state) => { state.isLoading = true; })
      .addCase(updateSupport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.supportsList = state.supportsList.map((support) =>
          support.id === action.payload.id ? action.payload : support
        );
        state.supportDetail = action.payload; // Update supportDetail if viewing
      })
      .addCase(updateSupport.rejected, (state) => { state.isLoading = false; })

      .addCase(deleteSupport.fulfilled, (state, action) => {
        state.supportsList = state.supportsList.filter((support) => support.id !== action.payload);
      });
  },
});

export default supportSlice.reducer;